import {neverland as $, render, html, useState, useEffect} from 'neverland';
import {isAdmin} from '../lib/cognito.js';
import Select from './Select.js';
import LoadingProgressBar from './LoadingProgressBar.js';
import * as CryptoJS from 'crypto-js';

const archive_filters = [{"archived": false}, {archived: {"$exists": false}}];

const CSVCreator = $(function(auth) {
	
	const [state, setState] = useState({
		items: [],
		clientGroupsData: [],
		clientGroups: [],
		itemSetKeys: [],
		itemSets: [],
		surveys: [],
		surveyLoadError: null,

		
		excludedClientGroups: [],
		selectedClientGroups: [],
		selectedItemSet: null,
		startDate: '',
		endDate: '',

		includeRotators: false,
		includeComments: false,
		includeAccessCode: false,
		matchBySurveyName: false,
		
		loading: true,
		loadingMessage: 'Loading reference data...',
		progress: 0,
		responseCount: null,
		isDownloading: false
	});

	// Load reference data on component mount
	useEffect(() => {
		loadReferenceData();
	}, []);

	async function loadReferenceData() {
		
		try {
			// Load items (exclude archived)
			let items = [];
			try {
				const itemsData = await auth.db.collection('Items').find({"$or": archive_filters});
				if (Array.isArray(itemsData)) {
					items = itemsData.reduce((acc, d) => {acc[d._id["$oid"].toString()] = d; return acc;}, {});
				}
				else {
					console.warn('No items found');
				}
			} catch (err) {
				console.warn('Could not load items:', err);
			}
			setState(prev => ({...prev, progress: 25}));

			// Load client groups (exclude archived)
			let clientGroupsData = [];
			let clientGroups = [];
			try {
				clientGroupsData = await auth.db.collection('ClientGroups').find({$or: archive_filters});
				if (Array.isArray(clientGroupsData)) {
					clientGroupsData = clientGroupsData
						.reverse()	
						.map(d => ({
							id: d._id["$oid"].toString(),
							value: d._id["$oid"].toString(),
							label: d.name,
							postcode: d.hasOwnProperty('postcode') ? d.postcode : null,
							permissions: d.hasOwnProperty('permissions') ? d.permissions : []
						}));
					clientGroups = (
						isAdmin() ?
						clientGroupsData :
						clientGroupsData.filter(g => g.permissions && g.permissions.includes('enableCSVCreator'))
					)
				}
				else {
					console.warn('No client groups found');
				}
			} catch (err) {
				console.warn('Could not load client groups:', err);
			}
			setState(prev => ({...prev, progress: 50 }));

			// Load itemsets (only those with 'Survey' in name)
			let itemSets = [];
			let itemSetKeys = {};
			try {
				const itemSetsData = await auth.db.collection('ItemSets').find({});
				if (Array.isArray(itemSetsData)) {
					itemSets = itemSetsData
						.filter(d => 
							d.name.includes('Survey') && 
							d.hasOwnProperty('hasResponses') && d.hasResponses &&
							(!d.hasOwnProperty('archived') || d.archived == false) &&
							d.version != 'Code-Dev'
						)
						.reverse()
						.map(d => ({
							id: d._id["$oid"].toString(),
							value: d._id["$oid"].toString(),
							label: `${d.name} [${d.version}]`,
							name: d.name
						}));
					function fetchItemFromItemset (ele, e) {
						if(e.collection == 'Items' && items.hasOwnProperty(e.element) && !['paragraph', 'calculator', 'error'].includes(items[e.element].type.toLowerCase())){	
							ele[e.element] = items[e.element].key;
						}
						else if (e.collection == 'ItemSets'){
							ele = {
								...ele, 
								...itemSetsData.find(its => its._id["$oid"].toString() == e.element).elements.reduce(fetchItemFromItemset, {})
							}
						}
						return ele;
					}
					itemSetKeys = itemSetsData.reduce((acc, d) => {
						
						acc[d._id["$oid"].toString()] = d.elements.reduce(fetchItemFromItemset, {});
						return acc;
					}, {});
				}
			} catch (err) {
				console.warn('Could not load itemsets:', err);
			}
			
			setState(prev => ({...prev, progress: 75 }));

			// Load surveys (exclude archived and test surveys)
			let surveys = [];
			let surveyLoadError = null;
			try {
				let survey_filters = {
					$and: [
						{$or: archive_filters},
						{$or: [
							{accessClientGroups: {$in: clientGroups.map(cg => cg.id)}},
							{dataClientGroup: {$in: clientGroups.map(cg => cg.id)}}
						]}
					], 
					type: 'real', 
					itemSet: {$in: itemSets.map(is => is.id)}
				};

				// Only the fields the selector + CSV builder actually use. The full
				// survey documents are large and, for an admin (whose clientGroups
				// list spans every school), the unprojected result for all ~11k+
				// surveys exceeds AWS Lambda's 6MB synchronous response limit — the
				// request then returns an { errorMessage } object instead of an
				// array, `state.surveys` stays empty, and every response-count comes
				// back 0. Projecting + paginating keeps each page under the limit.
				const survey_projection = {
					name: 1, itemSet: 1, dataClientGroup: 1,
					accessClientGroups: 1, closeDate: 1, openDate: 1, accessCode: 1
				};

				// Adaptive chunk sizing, mirroring the CSV download loop and the
				// reporting portal's _chunkedFind: halve the page on a payload-size
				// overflow and retry the SAME skip window rather than aborting.
				const MIN_CHUNK_SIZE = 50;
				const MAX_WINDOW_RETRIES = 8;
				let chunkSize = 2000;
				let skip = 0;

				const isPayloadSizeError = value => {
					const msg = (value && typeof value === 'object' && value.errorMessage)
						? value.errorMessage
						: (value && value.message) || '';
					return typeof msg === 'string' && msg.includes('payload size');
				};

				let done = false;
				while (!done) {
					let page = null;
					for (let attempt = 0; attempt < MAX_WINDOW_RETRIES; attempt++) {
						const result = await auth.db.collection('Surveys').find(
							survey_filters,
							{ ...survey_projection, limit: chunkSize, skip }
						);
						// Payload-size overflow comes back as a 200 with an
						// { errorMessage } body, not an array.
						if (!Array.isArray(result) && result && result.hasOwnProperty('errorMessage')) {
							if (isPayloadSizeError(result) && chunkSize > MIN_CHUNK_SIZE) {
								chunkSize = Math.max(MIN_CHUNK_SIZE, Math.floor(chunkSize / 2));
								continue; // retry same skip with a smaller window
							}
							// Non-recoverable (or already at the floor) — surface it.
							throw new Error(result.errorMessage || 'Survey load failed');
						}
						page = Array.isArray(result) ? result : [];
						break;
					}

					if (!Array.isArray(page)) {
						// Exhausted retries without a usable page.
						throw new Error('Survey load failed: payload size could not be reduced below the limit');
					}

					surveys = surveys.concat(page.map(d => ({
						id: d._id["$oid"].toString(),
						value: d._id["$oid"].toString(),
						label: d.name,
						itemSet: d.itemSet,
						dataClientGroup: d.dataClientGroup,
						accessClientGroups: d.accessClientGroups,
						closeDate: d.closeDate,
						openDate: d.openDate,
						accessCode: d.accessCode
					})));

					// A short page means we've reached the end.
					if (page.length < chunkSize) {
						done = true;
					} else {
						skip += page.length;
					}
				}
			} catch (err) {
				console.error('Could not load surveys:', err);
				// Do NOT leave this silent: an empty survey list makes every
				// response count come back 0 with no explanation. Record it so the
				// UI can tell the user the reference data failed to load.
				surveyLoadError = (err && err.message) || 'Could not load surveys';
				surveys = [];
			}

			if (surveyLoadError) {
				alert('Failed to load survey reference data: ' + surveyLoadError +
					'\n\nResponse counts and CSV downloads will not work until this is resolved. Please reload the page or contact support if it persists.');
			}

			setState(prev => ({
				...prev,
				items,
				clientGroupsData,
				clientGroups,
				itemSetKeys,
				itemSets,
				surveys,
				surveyLoadError,
				loading: false,
				progress: 0
			}));
		} catch (error) {
			console.error('Error loading reference data:', error);
			// Set fallback data
			setState(prev => ({
				...prev,
				items,
				clientGroupsData: [],
				clientGroups: [],
				itemSetKeys: {},
				itemSets: [],
				surveys: [],
				loading: false,
				progress: 0
			}));
		}
	}

	async function getResponseCount() {
		if (!state.selectedClientGroups || !state.selectedItemSet || !state.startDate) {
			alert('Please select a Client Group, Item Set and start date before trying to check response count!');
			return;
		}
		setState(prev => ({...prev, loading: true, loadingMessage: 'Counting responses...'}));
		
		try {
			const filters = buildFilters();
			const count = await auth.db.collection('Responses').count(filters);
			
			// Handle database errors
			if (typeof count === 'object' && count.hasOwnProperty('errorMessage')) {
				alert("Database Error: " + count.errorMessage + "\n\nThe page will be reset. Please try again with different filters or contact support if the error persists.");
				// Reset the component state
				setState(prev => ({
					...prev,
					selectedClientGroups: [],
					excludedClientGroups: [],
					selectedItemSet: null,
					startDate: '',
					endDate: '',
					includeRotators: false,
					includeComments: false,
					matchBySurveyName: false,
					responseCount: null,
					loading: false,
					progress: 0
				}));
				return;
			}
			
			setState(prev => ({...prev, responseCount: count, loading: false}));
		} catch (error) {
			console.error('Error counting responses:', error);
			setState(prev => ({...prev, loading: false}));
		}
	}

	function buildFilters() {
		const filters = {};
		const itemset_name = state.itemSets.find(is => is.id == state.selectedItemSet)['name']

		if(!state.matchBySurveyName){
			filters.itemSet = state.selectedItemSet;
		}
		filters.survey = {$in: state.surveys.filter(s => 
			(s.itemSet === state.selectedItemSet || (state.matchBySurveyName && s.label.includes(itemset_name))) && 
			(
				(state.selectedClientGroups.includes(s.dataClientGroup) && !state.excludedClientGroups.includes(s.dataClientGroup)) ||
				s.accessClientGroups.some(cg => state.selectedClientGroups.includes(cg) && !state.excludedClientGroups.includes(cg))
			) &&
			(!state.endDate || (s.openDate <= state.endDate)) &&
			s.closeDate >= state.startDate
		).map(s => s.id)};
		filters["fromDex"] = {"$exists": false};
		filters["$or"] = archive_filters;

		// Sentinel date used when a document's timestamp can't be parsed.
		// Some Responses have malformed timestamps (e.g. the bare string "2022")
		// where $substrCP yields "" and $dateFromString would otherwise throw a
		// ConversionFailure that aborts the ENTIRE query. Falling back to the
		// Unix epoch means such rows simply fail the date-range comparison and
		// are excluded, instead of crashing the count/download.
		const unparseableTimestampFallback = {
			"$dateFromString": {
				"dateString": "1970-01-01",
				"format": "%Y-%m-%d"
			}
		};
		// Parse the date portion out of the document's timestamp string, tolerating bad values.
		const parsedResponseDate = {
			"$dateFromString": {
				"dateString": {
					"$substrCP": ["$timestamp", 4, 11]
				},
				"format": "%b %d %Y",
				"onError": unparseableTimestampFallback,
				"onNull": unparseableTimestampFallback
			}
		};

		filters["$and"] = [ 
			{	
				"$expr": {
					"$gte": [
						parsedResponseDate,
						{
							"$dateFromString": {
								"dateString": state.startDate,
								"format": "%Y-%m-%d"
							}
						}
					]
				}
			}
		];
		if(state.endDate){
			filters["$and"].push(
				{
					"$expr": {
						"$lte": [
							parsedResponseDate,
							{
								"$dateFromString": {
									"dateString": state.endDate,
									"format": "%Y-%m-%d"
								}
							}
						]
					}
				}
			);
		}
		
		return filters;
	}

	async function generateDataDictionary() {
		if (!state.selectedItemSet) {
			alert('Please select an Item Set before generating data dictionary');
			return;
		}

		setState(prev => ({...prev, loading: true, loadingMessage: 'Generating data dictionary...', progress: 0}));
		
		try {
			const selectedItemset = state.itemSets.find(is => is.id == state.selectedItemSet);
			const itemSetData = await auth.db.collection('ItemSets').find({_id: {"$oid": state.selectedItemSet}});
			
			if (!itemSetData) {
				alert('Could not retrieve itemset data');
				setState(prev => ({...prev, loading: false}));
				return;
			}

			setState(prev => ({...prev, progress: 25, loadingMessage: 'Processing items...'}));
			
			const items = await getItemsFromItemSet(itemSetData[0]);
			
			setState(prev => ({...prev, progress: 75, loadingMessage: 'Creating Excel file...'}));
			
			const workbook = createDataDictionaryWorkbook(items, selectedItemset);
			
			setState(prev => ({...prev, progress: 100, loadingMessage: 'Download ready...'}));
			
			setTimeout(() => {
				setState(prev => ({...prev, loading: false, progress: 0}));
			}, 500);
			
		} catch (error) {
			console.error('Error generating data dictionary:', error);
			alert('Error generating data dictionary: ' + error.message);
			setState(prev => ({...prev, loading: false}));
		}
	}

	async function getItemsFromItemSet(itemSet) {
		const types = {
			'choose-1': 'Radio Type',
			'choose-n': 'Radio Type', 
			'dropdown-1': 'Dropdown Box',
			'text-area': 'Big Text Box'
		};

		const items = [];
		
		for (const itemObj of itemSet.elements) {
			try {
				if (itemObj.collection === 'Items') {
					const item = state.items[itemObj.element];
					if (item && item.type !== 'paragraph') {
						const processedItem = {
							key: item.key,
							prompt: item.prompt || '',
							caption: item.caption || '',
							type: types[item.type] || item.type,
							placeholder: item.placeholder || ''
						};
						
						if (itemObj.conditions && itemObj.conditions.length > 0) {
							const conditions = [];
							for (const condObj of itemObj.conditions) {
								const condItem = state.items[condObj.item];
								if (condItem) {
									conditions.push(`${condItem.key}${condObj.condition}"${condObj.value}"`);
								}
							}
							const operator = itemObj.conditionAggregator === 'one' ? ' OR ' : ' AND ';
							processedItem.conditions = conditions.join(operator);
						}
						
						if (item.options) {
							const optionsStr = item.options.map(opt => `[${opt.value}]${opt.label}`).join(', ');
							processedItem.options = optionsStr.length > 32767 ? '> too many options to fit here <' : optionsStr;
						}
						
						items.push(processedItem);
					}
				} else if (itemObj.collection === 'ItemSets') {
					const nestedItemSet = await auth.db.collection('ItemSets').find({_id: {"$oid": itemObj.element}});
					if (nestedItemSet[0]) {
						const nestedItems = await getItemsFromItemSet(nestedItemSet[0]);
						items.push(...nestedItems);
					}
				}
			} catch (error) {
				console.warn('Error processing item:', error);
			}
		}
		
		return items;
	}

	function createDataDictionaryWorkbook(items, selectedItemset) {
		const headers = ['TECHNICAL_NAME', 'NOTES', 'LABEL', 'DESCRIPTION', 'TYPE', 'OPTIONS', 'PLACEHOLDER'];
		const columns = ['key', 'conditions', 'prompt', 'caption', 'type', 'options', 'placeholder'];
		
		// Create CSV content
		const csvRows = [headers.join(',')];
		
		items.forEach(item => {
			const row = columns.map(col => {
				let value = item[col] || '';
				// Escape CSV values
				if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
					value = `"${value.replace(/"/g, '""')}"`;
				}
				return value;
			});
			csvRows.push(row.join(','));
		});
		
		const csvContent = csvRows.join('\n');
		const filename = `DataDictionary-${selectedItemset.name}-${selectedItemset.label.match(/\[(.+)\]/)?.[1] || 'version'}.csv`;
		
		// Download CSV
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', filename);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	async function downloadCSV() {
		if (!state.selectedClientGroups || !state.selectedItemSet || !state.startDate) {
			alert('Please select a Client Group, Item Set and start date before trying to download CSV');
			return;
		}

		setState(prev => ({...prev, isDownloading: true, loading: true, loadingMessage: 'Preparing download...', progress: 0}));
		
		try {
			const filters = buildFilters();
			const selectedItemset = state.itemSets.find(is => is.id == state.selectedItemSet)
			const end_date = state.endDate ? `_to_${state.endDate}`: '';
			const filename = `${selectedItemset.label}_data_from_${state.startDate}${end_date}.csv`;
			
			// Get total count for progress tracking
			const totalCount = await auth.db.collection('Responses').count(filters);
			
			if (totalCount === 0) {
				alert('No responses found matching the selected filters');
				setState(prev => ({...prev, isDownloading: false, loading: false}));
				return;
			}

			// Log the download request
			await logDownloadRequest({
				filename,
				filters,
				selectedItemset: selectedItemset.label,
				clientGroups: state.selectedClientGroups,
				excludedClientGroups: state.excludedClientGroups,
				startDate: state.startDate,
				endDate: state.endDate,
				includeRotators: state.includeRotators,
				includeComments: state.includeComments,
				matchBySurveyName: state.matchBySurveyName,
				responseCount: totalCount
			});
			
			// First pass: collect all unique headers
			setState(prev => ({...prev, loadingMessage: 'Analyzing data structure...', progress: 10}));
			const headers = Object.values(state.itemSetKeys[state.selectedItemSet]);
			headers.unshift(...['school', 'timestamp', 'postcode', 'accessCode'])
			if (state.includeRotators) headers.push('rotators');
			
			// Create a writable stream for the CSV
			const stream = new WritableStream({
				write(chunk) {
					this.chunks = this.chunks || [];
					this.chunks.push(chunk);
				},
				close() {
					const blob = new Blob(this.chunks, {type: 'text/csv;charset=utf-8;'});
					const link = document.createElement('a');
					const url = URL.createObjectURL(blob);
					link.setAttribute('href', url);
					link.setAttribute('download', filename);
					link.style.visibility = 'hidden';
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				}
			});
			
			const writer = stream.getWriter();
			
			// Write headers
			await writer.write(headers.join(',') + '\n');
			
			// Second pass: write data with complete headers
			let skip = 0;
			// Adaptive chunk sizing. Responses carry large `answers` arrays, so a
			// fixed page can exceed AWS Lambda's 6MB synchronous response limit
			// ("Response payload size exceeded maximum allowed payload size").
			// When we hit that limit we halve the chunk size and retry the SAME
			// skip window rather than aborting the whole download.
			const MIN_CHUNK_SIZE = 10;
			const MAX_WINDOW_RETRIES = 8;
			let chunkSize = 1000;

			// True when the DB response is Lambda's payload-size overflow. This
			// comes back as a 200 with an { errorMessage } body (not an HTTP
			// error), so we sniff the message text.
			const isPayloadSizeError = value => {
				const msg = (value && typeof value === 'object' && value.errorMessage)
					? value.errorMessage
					: (value && value.message) || '';
				return typeof msg === 'string' && msg.includes('payload size');
			};

			while (skip < totalCount) {
				setState(prev => ({
					...prev, 
					progress: 20 + Math.round((skip / totalCount) * 70),
					loadingMessage: `Writing CSV... ${skip}/${totalCount}`
				}));

				let chunk = null;
				let payloadTooLarge = false;

				// Try the current window, shrinking chunkSize on payload-size
				// errors until it fits or we hit the floor / retry cap.
				for (let attempt = 0; attempt < MAX_WINDOW_RETRIES; attempt++) {
					try {
						const result = await auth.db.collection('Responses').find(filters, {limit: chunkSize, skip});

						// Payload-size overflow returned as an { errorMessage } object.
						if (!Array.isArray(result) && result && result.hasOwnProperty('errorMessage')) {
							if (isPayloadSizeError(result) && chunkSize > MIN_CHUNK_SIZE) {
								chunkSize = Math.max(MIN_CHUNK_SIZE, Math.floor(chunkSize / 2));
								payloadTooLarge = true;
								continue; // retry same skip with a smaller window
							}
							// Non-recoverable DB error (or already at floor) — surface it.
							chunk = result;
							break;
						}

						chunk = result;
						payloadTooLarge = false;
						break;
					} catch (fetchErr) {
						// A thrown payload-size error is treated the same way.
						if (isPayloadSizeError(fetchErr) && chunkSize > MIN_CHUNK_SIZE) {
							chunkSize = Math.max(MIN_CHUNK_SIZE, Math.floor(chunkSize / 2));
							payloadTooLarge = true;
							continue;
						}
						throw fetchErr; // let the outer catch handle unexpected errors
					}
				}

				// Error conditions that must halt (not silently truncate) the CSV:
				//   a) still too large even at the minimum chunk size,
				//   b) a real (non payload-size) DB error object was returned, or
				//   c) retries were exhausted without ever getting a valid array.
				if ((payloadTooLarge && chunkSize <= MIN_CHUNK_SIZE) ||
					(!Array.isArray(chunk) && chunk && chunk.hasOwnProperty('errorMessage')) ||
					(!Array.isArray(chunk))) {
					const detail = (chunk && chunk.errorMessage)
						? chunk.errorMessage
						: `Records at position ${skip} could not be retrieved (they may be too large even individually).`;
					alert("Database Error: " + detail + "\n\nThe page will be reset. Please try again with different filters or contact support if the error persists.");
					// Reset the component state
					setState(prev => ({
						...prev,
						selectedClientGroups: [],
						excludedClientGroups: [],
						selectedItemSet: null,
						startDate: '',
						endDate: '',
						includeRotators: false,
						includeComments: false,
						matchBySurveyName: false,
						responseCount: null,
						isDownloading: false,
						loading: false,
						progress: 0
					}));
					return;
				}

				// Reached the end of the result set.
				if (chunk.length === 0) break;

				// Process chunk with complete headers
				const csvChunk = processResponseChunk(chunk, state.itemSetKeys[state.selectedItemSet], headers);
				await writer.write(csvChunk);

				// Advance by however many records we actually retrieved this window
				// (chunkSize may have shrunk mid-download).
				skip += chunk.length;
			}
			
			setState(prev => ({...prev, loadingMessage: 'Finalizing download...', progress: 95}));
			
			await writer.close();
			
			setState(prev => ({...prev, isDownloading: false, loading: false, progress: 100}));
		} catch (error) {
			console.error('Error downloading CSV:', error);
			setState(prev => ({...prev, isDownloading: false, loading: false}));
		}
	}

	// Function to create a md5 hash of a string
	function md5(str) {
		return CryptoJS.MD5(str).toString();
	}

	async function logDownloadRequest(requestDetails) {
		try {
			await auth.db.collection('CSVDownloads').insertOne({
				timestamp: new Date().toLocaleString('en-AU'),
				action: 'CSV_DOWNLOAD',
				userEmail: auth.userEmail,
				userGroups: auth.userGroups,
				userRole: auth.currentRole?.split('/').pop(),
				...requestDetails
			});
		} catch (error) {
			console.error('Failed to log download request:', error);
		}
	}

	function processResponseChunk(responses, itemSetKeys, headers) {
		if (!Array.isArray(responses) || responses.length === 0) return '';
		
		// Process responses
		const processedResponses = responses.map(d => {
			let response = {};
			let survey = state.surveys.find(s => s.id == d.survey);
			let client_id = survey.dataClientGroup;
			let client = state.clientGroupsData.find(c => c.id == client_id);
			response.school = md5(client_id)
			response.timestamp = d.timestamp;
			response.postcode = client.hasOwnProperty('postcode') ? client['postcode'] : '';
			if(state.includeAccessCode){
				response.accessCode = survey.accessCode;
			}
			if (state.includeRotators) {
				response.rotators = {}
			}
			d.answers.forEach(a => {
				// Skip answers whose item is not in the loaded item map. Items are
				// loaded excluding archived ones, but historical responses can
				// reference items that have since been archived/deleted — those
				// have no key to map to a CSV column, so we can't include them.
				const itemDef = state.items[a.item];
				if (!itemDef) {
					return;
				}

				if(!itemSetKeys.hasOwnProperty(a.item) || !headers.includes(itemDef.key)){
					if(state.includeRotators){
						response.rotators[itemDef.key] = a.answer
					}
					else {
						return;
					}
				}
				
				if (response.hasOwnProperty(itemSetKeys[a.item])) {
					return;
				}
				
				if (itemDef['type'].toLowerCase().startsWith('text') && !state.includeComments){
					return;
				}

				// store answer in response, if it is string, remove line spacing or any white spaces and trim before storing
				response[itemSetKeys[a.item]] = (
					a.answer && typeof a.answer === 'string' ? 
					a.answer.replace(/\s+/g, ' ').trim() : 
					(a.answer && Array.isArray(a.answer) ? (
						itemDef.key == 'culture' ?
						a.answer.map(ans => itemDef["options"].find(opt => opt.value == ans).label):
						a.answer
					).join(',') : a.answer)
				);
			});
			if (state.includeRotators) {
				response.rotators = JSON.stringify(response.rotators);
			}
			return response;
		});
		
		// Build CSV rows for this chunk
		const csvRows = [];
		processedResponses.forEach(response => {
			const row = headers.map(header => {
				const value = response.hasOwnProperty(header) ? (response[header] || ''): '';
				// Escape commas and quotes in CSV
				if (header != 'rotators' && typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
					return `"${value.replace(/"/g, '""')}"`;
				}
				return value;
			});
			csvRows.push(row.join(','));
		});
		
		return csvRows.join('\n') + (csvRows.length > 0 ? '\n' : '');
	}



	return html`
		<div class="main">
			<div class="controls">
				<h2>CSV Creator</h2>
			</div>

			<div class="csv-creator-form">
				<div class="filter-section">
					<h3>Filters</h3>
					
					<div 
						class="filter"
						title="[Required] ClientGroup or school whose Survey Responses need to be downloaded"
					>
						<label>Selected Client Groups</label>
						${Select(
							state.clientGroups,
							state.selectedClientGroups,
							val => setState(prev => ({...prev, selectedClientGroups: val})),
							false,
							[],
							true,
							true
						)}
					</div>

					<div 
						class="filter"
						title="[Optional] To exclude subset of response from a different client"
					>
						<label>Excluded Client Groups (Optional)</label>
						${Select(
							state.clientGroups,
							state.excludedClientGroups,
							val => setState(prev => ({...prev, excludedClientGroups: val})),
							false,
							[],
							true,
							true
						)}
					</div>

					<div 
						class="filter"
						title="[Required] this will apply then end date as a filter for both the Surveys (by close date) and Responses (by timestamp)"
					>
						<label>ItemSet</label>
						${Select(
							state.itemSets,
							state.selectedItemSet,
							val => setState(prev => ({...prev, selectedItemSet: val})),
							false,
							[],
							true,
							false
						)}
					</div>

					<div class="filter">
						<label>Start Date</label>
						<input 
							type="date" 
							value=${state.startDate}
							max=${new Date().toISOString().split('T')[0]}
							onchange=${e => {
								const today = new Date().toISOString().split('T')[0];
								if (e.target.value > today) {
									alert('Start date cannot be later than today');
									e.target.value = today;
									setState(prev => ({...prev, startDate: today, responseCount: null}));
								} else {
									setState(prev => ({...prev, startDate: e.target.value, responseCount: null}));
								}
							}}
							title="If provided, this will apply the start date as a filter for both the Surveys (by open date) and Responses (by timestamp)"
						/>
					</div>

					<div class="filter">
						<label>End Date (Optional)</label>
						<input 
							type="date" 
							value=${state.endDate}
							max=${new Date().toISOString().split('T')[0]}
							onchange=${e => {
								const today = new Date().toISOString().split('T')[0];
								if (e.target.value > today) {
									alert('End date cannot be later than today');
									e.target.value = today;
									setState(prev => ({...prev, endDate: today, responseCount: null}));
								} else if (state.startDate && e.target.value < state.startDate) {
									alert('End date cannot be earlier than start date');
									e.target.value = state.startDate;
									setState(prev => ({...prev, endDate: state.startDate, responseCount: null}));
								} else {
									setState(prev => ({...prev, endDate: e.target.value, responseCount: null}));
								}
							}}
							title="If provided, this will apply the end date as a filter for both the Surveys (by close date) and Responses (by timestamp)"
						/>
					</div>



					<div class="toggle-section">
						<label
							title="If enabled, rotator items (items that are randomly selected from a group of items), if available in the itemset, will be included in the CSV"
						>
							<input 
								type="checkbox" 
								?checked=${state.includeRotators}
								onchange=${e => setState(prev => ({...prev, includeRotators: e.target.checked}))}
							/>
							Include rotator items
						</label>
						
						<label
							title="If enabled, comments or other textual response items will be included in the CSV"
						>
							<input 
								type="checkbox" 
								?checked=${state.includeComments}
								onchange=${e => setState(prev => ({...prev, includeComments: e.target.checked}))}
							/>
							Include comments
						</label>
						
						<label
							title="If enabled, Access code of Survey will be included in the CSV"
						>
							<input 
								type="checkbox" 
								?checked=${state.includeAccessCode}
								onchange=${e => setState(prev => ({...prev, includeAccessCode: e.target.checked}))}
							/>
							Include access code
						</label>
						
						<label
							title="If enabled, will include surveys that have the same name as the selected item set, but may belong to different item set versions."
						>
							<input 
								type="checkbox" 
								?checked=${state.matchBySurveyName}
								onchange=${e => setState(prev => ({...prev, matchBySurveyName: e.target.checked}))}
							/>
							Match by Survey Name [Multiple Years]
						</label>
					</div>
				</div>

				<div class="action-section">
					<button 
						class="blue" 
						onclick=${getResponseCount}
						?disabled=${!state.selectedItemSet || !state.startDate || state.loading}
					>
						Get Response Count
					</button>
					
					${state.responseCount !== null ? html`
						<div class="count-display">
							Found ${state.responseCount} responses
						</div>
					` : ''}
					
					<button 
						class="green" 
						onclick=${downloadCSV}
						?disabled=${!state.selectedItemSet || !state.startDate || state.loading || state.isDownloading}
					>
						Download CSV
					</button>
					
					<button 
						class="blue" 
						onclick=${generateDataDictionary}
						?disabled=${!state.selectedItemSet || state.loading}
						title="Generate a data dictionary for the selected itemset"
					>
						Generate Data Dictionary
					</button>
				</div>
			</div>

			<div class="help-note">
				💡 <em>Tip: Hover over any filter or checkbox above to see helpful information about what it does.</em>
			</div>

			${state.loading ? html`
				<div class="loading-overlay">
					${LoadingProgressBar(state.progress, state.loadingMessage)}
				</div>
			` : ''}
		</div>
	`;
});

export default CSVCreator;